const INTEGER_PATTERN = /^(0|[1-9]\d*)$/;
const YUAN_PATTERN = /^(0|[1-9]\d*)(?:\.(\d{1,2}))?$/;
const SIGNED_YUAN_PATTERN = /^-?(0|[1-9]\d*)(?:\.(\d{1,2}))?$/;
export class MoneyError extends Error {
    constructor(message) {
        super(message);
        this.name = "MoneyError";
    }
}
export function parseMinor(value) {
    if (!INTEGER_PATTERN.test(value)) {
        throw new MoneyError("金额必须是非负整数分字符串");
    }
    return BigInt(value);
}
function yuanToMinor(match) {
    const yuan = BigInt(match[1] ?? "0");
    const fraction = (match[2] ?? "").padEnd(2, "0");
    return yuan * 100n + BigInt(fraction || "0");
}
export function parseYuan(value) {
    const match = YUAN_PATTERN.exec(value.trim());
    if (!match) {
        throw new MoneyError("请输入有效的人民币金额，最多两位小数");
    }
    return yuanToMinor(match);
}
export function parseSignedYuan(value) {
    const trimmed = value.trim();
    const match = SIGNED_YUAN_PATTERN.exec(trimmed);
    if (!match) {
        throw new MoneyError("请输入有效的人民币金额，最多两位小数");
    }
    const amount = yuanToMinor(match);
    return trimmed.startsWith("-") ? -amount : amount;
}
export function formatYuan(value) {
    const sign = value < 0n ? "-" : "";
    const absolute = value < 0n ? -value : value;
    const yuan = absolute / 100n;
    const cents = (absolute % 100n).toString().padStart(2, "0");
    return `${sign}${yuan.toString()}.${cents}`;
}
export function formatCny(value) {
    const formatted = formatYuan(value);
    return formatted.startsWith("-") ? `-¥${formatted.slice(1)}` : `¥${formatted}`;
}
export function roundHalfUp(numerator, denominator) {
    if (numerator < 0n || denominator <= 0n) {
        throw new MoneyError("四舍五入仅接受非负分子和正分母");
    }
    return (numerator * 2n + denominator) / (denominator * 2n);
}
export function calculateDiscount(originalAmount, discount) {
    if (originalAmount < 0n)
        throw new MoneyError("原始金额不能为负数");
    if (discount.type === "FIXED") {
        if (discount.value < 0n)
            throw new MoneyError("优惠金额不能为负数");
        return discount.value > originalAmount ? originalAmount : discount.value;
    }
    if (discount.rateBps < 0n || discount.rateBps > 10000n) {
        throw new MoneyError("折扣基点必须在 0 到 10000 之间");
    }
    let amount = roundHalfUp(originalAmount * discount.rateBps, 10000n);
    if (discount.maximumAmount !== undefined && amount > discount.maximumAmount) {
        amount = discount.maximumAmount;
    }
    return amount > originalAmount ? originalAmount : amount;
}
export function availableSpendable(normalBalance, bonusBalance) {
    const normal = normalBalance > 0n ? normalBalance : 0n;
    const bonus = bonusBalance > 0n ? bonusBalance : 0n;
    return normal + bonus;
}
export function creditDebt(normalBalance, bonusBalance) {
    const total = normalBalance + bonusBalance;
    return total < 0n ? -total : 0n;
}
export function exceedsCreditLimit(normalBalance, bonusBalance, creditMaxCents) {
    if (creditMaxCents <= 0n)
        return false;
    return creditDebt(normalBalance, bonusBalance) >= creditMaxCents;
}
export function splitBalance(normalBalance, bonusBalance, consumeAmount) {
    if (bonusBalance < 0n || consumeAmount < 0n) {
        throw new MoneyError("赠金和消费金额不能为负数");
    }
    if (consumeAmount === 0n)
        return { normalUsed: 0n, bonusUsed: 0n };
    if (normalBalance < 0n)
        throw new MoneyError("用户余额不足");
    const totalBalance = normalBalance + bonusBalance;
    if (consumeAmount > totalBalance)
        throw new MoneyError("用户余额不足");
    if (totalBalance === 0n)
        throw new MoneyError("用户余额不足");
    const roundedNormal = roundHalfUp(consumeAmount * normalBalance, totalBalance);
    const minimumNormal = consumeAmount > bonusBalance ? consumeAmount - bonusBalance : 0n;
    const maximumNormal = normalBalance < consumeAmount ? normalBalance : consumeAmount;
    const normalUsed = roundedNormal < minimumNormal
        ? minimumNormal
        : roundedNormal > maximumNormal
            ? maximumNormal
            : roundedNormal;
    const bonusUsed = consumeAmount - normalUsed;
    if (normalUsed < 0n ||
        bonusUsed < 0n ||
        normalUsed > normalBalance ||
        bonusUsed > bonusBalance ||
        normalUsed + bonusUsed !== consumeAmount) {
        throw new MoneyError("余额分摊不变量被破坏");
    }
    return { normalUsed, bonusUsed };
}
export function splitBalanceForCharge(normalBalance, bonusBalance, consumeAmount, allowCredit) {
    if (bonusBalance < 0n || consumeAmount < 0n) {
        throw new MoneyError("赠金和消费金额不能为负数");
    }
    if (consumeAmount === 0n)
        return { normalUsed: 0n, bonusUsed: 0n };
    const spendable = availableSpendable(normalBalance, bonusBalance);
    if (consumeAmount <= spendable && normalBalance >= 0n) {
        return splitBalance(normalBalance, bonusBalance, consumeAmount);
    }
    if (consumeAmount <= bonusBalance) {
        return { normalUsed: 0n, bonusUsed: consumeAmount };
    }
    if (!allowCredit)
        throw new MoneyError("用户余额不足");
    return {
        normalUsed: consumeAmount - bonusBalance,
        bonusUsed: bonusBalance,
    };
}
/**
 * 入账后重算余额构成，避免「正常余额为负且赠金为正」。
 * - 合计仍负债：全部记在正常余额，赠金为 0
 * - 入账前无欠款：保持「正常/赠金」各自累加
 * - 入账前有欠款且合计转正：按本次入账正常金额与赠金，用 splitBalance 比例分配合计（与消费分摊同一套逻辑）
 */
export function allocateBalancesAfterCredit(previousNormal, previousBonus, creditNormal, creditBonus) {
    if (previousBonus < 0n || previousBonus + creditBonus < 0n) {
        throw new MoneyError("赠金不能为负数");
    }
    const rawNormal = previousNormal + creditNormal;
    const rawBonus = previousBonus + creditBonus;
    const afterTotal = rawNormal + rawBonus;
    const wasInDebt = previousNormal < 0n || previousNormal + previousBonus < 0n;
    if (afterTotal <= 0n) {
        return { normalBalance: afterTotal, bonusBalance: 0n };
    }
    if (!wasInDebt) {
        return { normalBalance: rawNormal, bonusBalance: rawBonus };
    }
    const weightNormal = creditNormal > 0n ? creditNormal : 0n;
    const weightBonus = (previousBonus > 0n ? previousBonus : 0n) + (creditBonus > 0n ? creditBonus : 0n);
    if (weightNormal === 0n) {
        return { normalBalance: 0n, bonusBalance: afterTotal };
    }
    if (weightBonus === 0n) {
        return { normalBalance: afterTotal, bonusBalance: 0n };
    }
    const split = splitBalance(weightNormal, weightBonus, afterTotal);
    return { normalBalance: split.normalUsed, bonusBalance: split.bonusUsed };
}
export { DEFAULT_TIME_BILLING, calculateBillingUnits, calculateTimeBilling, resolveCycleStartedAt, } from "./time-billing.js";
//# sourceMappingURL=index.js.map